import axios from "axios";
import { ENDPOINTS } from "../server/endpoints";
import { getCurrentLanguage } from "../core/i18n";
import { CACHE_TTL, withRequestCache } from "../core/cache/requestCache";
import {
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
  setAuthData,
  deleteAuthData
} from "../utils/auth";

const parseRetryAfterSeconds = (retryAfterHeader) => {
  if (!retryAfterHeader) {
    return null;
  }

  const normalized = String(retryAfterHeader).trim();
  if (!normalized) {
    return null;
  }

  const deltaSeconds = Number.parseInt(normalized, 10);
  if (!Number.isNaN(deltaSeconds)) {
    return Math.max(deltaSeconds, 1);
  }

  const retryDateMs = Date.parse(normalized);
  if (Number.isNaN(retryDateMs)) {
    return null;
  }

  const secondsUntilRetry = Math.ceil((retryDateMs - Date.now()) / 1000);
  return Math.max(secondsUntilRetry, 1);
};

const parseRetryAfterFromError = (error) => {
  const headerValue = error.response?.headers?.['retry-after'];
  const headerSeconds = parseRetryAfterSeconds(headerValue);
  if (headerSeconds) {
    return headerSeconds;
  }

  const bodyValue = Number(error.response?.data?.retryAfterSeconds);
  if (Number.isFinite(bodyValue) && bodyValue > 0) {
    return Math.ceil(bodyValue);
  }

  return null;
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Separate axios instance for refresh token to avoid interceptor loop
const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_URL,
  timeout: 30000,
});

// Function to refresh the token
const doRefreshToken = async () => {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error("No refresh token available");
  }

  const response = await refreshClient.post(ENDPOINTS.REFRESH, {
    refreshToken: refreshTokenValue
  });

  return response;
};

// No request-side Cache-Control: the server says what may be cached (API
// JSON never is; files carry an ETag and a year-long max-age), and a blanket
// `no-cache` here would make the browser revalidate every cached file.
const apiClient = withRequestCache(axios.create({
  baseURL: process.env.REACT_APP_URL,
  timeout: 100000,
  headers: {
    Accept: "*/*",
  },
}));

// Opt-in response caching for GETs — see core/cache/requestCache.js. Pass one
// of these as the request config: `apiClient.get(url, REFERENCE)`.
const REFERENCE = { cache: { ttl: CACHE_TTL.REFERENCE } };
const PROFILE = { cache: { ttl: CACHE_TTL.PROFILE } };
const SCREEN = { cache: { ttl: CACHE_TTL.SCREEN } };

apiClient.interceptors.request.use(
  async (config) => {
    // Tell the server which language to resolve dynamic content (course names,
    // announcements) into. Mirrors the in-app language the user picked.
    config.headers['Accept-Language'] = getCurrentLanguage();

    // Skip token refresh for refresh endpoint
    if (config.url === ENDPOINTS.REFRESH) {
      return config;
    }

    let token = getAccessToken();

    // If token exists and is expired, try to refresh
    if (token && isAccessTokenExpired() && getRefreshToken()) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshResponse = await doRefreshToken();

          if (refreshResponse?.data?.success) {
            setAuthData(refreshResponse.data.data);
            token = getAccessToken();
            processQueue(null, token);
          } else {
            processQueue(new Error("Refresh failed"), null);
            deleteAuthData();
            window.location.href = "/login";
            return Promise.reject(new Error("Refresh token invalid"));
          }
        } catch (error) {
          processQueue(error, null);
          deleteAuthData();
          window.location.href = "/login";
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (newToken) => {
              config.headers.Authorization = `Bearer ${newToken}`;
              resolve(config);
            },
            reject: (err) => {
              reject(err);
            }
          });
        });
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const statusCode = error.response?.status || null;
        const retryAfterSeconds = parseRetryAfterFromError(error);
        const backendMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Unexpected error";

        // Attach readable message without removing original details
        error.readableMessage = backendMessage;
        error.statusCode = statusCode;
        error.retryAfterSeconds = retryAfterSeconds;
        error.isRateLimited = statusCode === 429;

        // The month's AI allowance is spent. Distinct from an ordinary 429: it
        // does not clear by waiting a minute, so the caller needs the reset date
        // and whether a local parse is still on offer rather than a retry timer.
        error.isAiQuotaExceeded = statusCode === 429 && error.response?.data?.quotaExceeded === true;
        if (error.isAiQuotaExceeded) {
            error.aiQuota = {
                used: error.response?.data?.used,
                limit: error.response?.data?.limit,
                unit: error.response?.data?.unit,
                resetsOn: error.response?.data?.resetsOn,
                localFallbackAvailable: error.response?.data?.localFallbackAvailable === true,
            };
        }

        // An admin reset this account's password, so the server refuses
        // everything except changing it. Send the user there rather than showing
        // a permission error on whatever screen they happened to open.
        if (statusCode === 403 && error.response?.data?.mustChangePassword === true) {
            error.mustChangePassword = true;
            if (!window.location.pathname.endsWith("/change-password")) {
                window.location.href = "/change-password";
            }
        }

        // Handle 401 errors - try to refresh token
        if (statusCode === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const refreshTokenValue = getRefreshToken();
            if (refreshTokenValue && !isRefreshing) {
                isRefreshing = true;

                try {
                    const refreshResponse = await doRefreshToken();

                    if (refreshResponse?.data?.success) {
                        setAuthData(refreshResponse.data.data);
                        const newToken = getAccessToken();
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        isRefreshing = false;
                        return apiClient(originalRequest);
                    }
                } catch (refreshError) {
                    isRefreshing = false;
                    deleteAuthData();
                    window.location.href = "/login";
                    return Promise.reject(refreshError);
                }
            }

            deleteAuthData();
            window.location.href = "/login";
        }

        return Promise.reject(error);
    }
);


// ================================= AUTHENTICATION ========================================

export const login = (credentials) => apiClient.post(ENDPOINTS.LOGIN, credentials);
export const logout = () => apiClient.delete(ENDPOINTS.LOGOUT);
export const refreshToken = () => {
  return axios.post(
    process.env.REACT_APP_URL + ENDPOINTS.REFRESH,
    { refreshToken: getRefreshToken() }
  );
};

// ================================ BRANDING / PUBLIC CONFIG ===============================

/**
 * Unauthenticated. Uses a bare axios call rather than apiClient so it cannot be
 * caught by the token-refresh interceptor and bounced to /login — this is the
 * request the login screen itself makes.
 */
export const getPublicConfig = () =>
  axios.get(process.env.REACT_APP_URL + ENDPOINTS.PUBLIC_CONFIG, { timeout: 10000 });

export const getBrandingSettings = () => apiClient.get(ENDPOINTS.ADMIN_BRANDING);
export const updateBrandingSettings = (values) => apiClient.put(ENDPOINTS.ADMIN_BRANDING, values);
export const uploadBrandingLogo = (file, dark = false) => {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient.post(`${ENDPOINTS.ADMIN_BRANDING_LOGO}?dark=${dark}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// ====================================== AI USAGE =========================================

export const getAiUsage = () => apiClient.get(ENDPOINTS.ADMIN_AI_USAGE);

// ==================================== PASSWORD ===========================================

export const passwordResetRequest = (payload) => apiClient.post(ENDPOINTS.PASSWORD_RESET_REQUEST, payload);
export const passwordResetConfirm = (values) => apiClient.post(ENDPOINTS.PASSWORD_RESET_CONFIRM, values);
export const changePassword = (values) => apiClient.post(ENDPOINTS.PASSWORD_CHANGE, values);

/**
 * Issue a temporary password for another user. The response carries it in plain
 * text and is the only time it is readable, so callers must show it to the admin
 * immediately and never persist it.
 */
export const adminResetUserPassword = (userId) =>
  apiClient.post(ENDPOINTS.ADMIN_PASSWORD_RESET(userId));

// ====================================== ADMIN ============================================

export const getAdminDashboardStats = () => apiClient.get(ENDPOINTS.ADMIN_DASHBOARD, SCREEN);
export const getAdminExtendedDashboard = () => apiClient.get(ENDPOINTS.ADMIN_DASHBOARD + '/extended', SCREEN);
export const getCourseList = (searchText, page, size) => apiClient.get(ENDPOINTS.ADMIN_COURSE + "?keyword=" + searchText + "&page=" + page + "&size=" + size, SCREEN);
export const getCourseOptions = () => apiClient.get(ENDPOINTS.COURSE_OPTIONS, REFERENCE);

// ====================================== RESOURCES (library) ============================================

const buildResourceFormData = (payload, file) => {
  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(payload)], { type: "application/json" }),
  );
  if (file) {
    formData.append("file", file);
  }
  return formData;
};

export const getResources = ({ type, courseId, search, tag, page = 0, size = 12 } = {}) => {
  const params = new URLSearchParams();
  if (type) params.append("type", type);
  if (courseId !== undefined && courseId !== null && courseId !== "") {
    params.append("courseId", courseId);
  }
  if (search) params.append("search", search);
  if (tag) params.append("tag", tag);
  params.append("page", page);
  params.append("size", size);
  return apiClient.get(`${ENDPOINTS.RESOURCES}?${params.toString()}`, SCREEN);
};

export const getResource = (id) => apiClient.get(ENDPOINTS.RESOURCE(id), SCREEN);

export const getResourceTags = () => apiClient.get(ENDPOINTS.RESOURCE_TAGS, REFERENCE);

export const createResource = (payload, file) =>
  apiClient.post(ENDPOINTS.RESOURCES, buildResourceFormData(payload, file));

export const updateResource = (id, payload, file) =>
  apiClient.put(ENDPOINTS.RESOURCE(id), buildResourceFormData(payload, file));

export const deleteResource = (id) => apiClient.delete(ENDPOINTS.RESOURCE(id));
export const createCourse = (courseData) => apiClient.post(ENDPOINTS.ADMIN_COURSE, courseData, { headers: { "Content-Type": "application/json" } });
export const updateCourse = (courseId, courseData) => apiClient.put(ENDPOINTS.ADMIN_COURSE + `/${courseId}`, courseData, { headers: { "Content-Type": "application/json" } });
export const deleteCourse = (courseId) => apiClient.delete(ENDPOINTS.ADMIN_COURSE + `/${courseId}`);

export const getGroupList = (searchText, page, size) =>
  apiClient.get(ENDPOINTS.ADMIN_GROUP + `?keyword=${searchText}&page=${page}&size=${size}`, SCREEN);
export const getCourseIdAndName = () => apiClient.get(ENDPOINTS.ADMIN_GROUP + `/course-id-and-name`, REFERENCE);
export const getTeacherIdAndUsername = () => apiClient.get(ENDPOINTS.ADMIN_GROUP + `/teacher-id-and-username`, REFERENCE);
export const getRoomIdAndName = () => apiClient.get(ENDPOINTS.ADMIN_ROOM, REFERENCE);
export const createGroup = (groupData) => apiClient.post(ENDPOINTS.ADMIN_GROUP, groupData);
export const updateGroup = (groupId, groupData) => apiClient.put(ENDPOINTS.ADMIN_GROUP + `/${groupId}`, groupData);
export const deleteGroup = (groupId) => apiClient.delete(ENDPOINTS.ADMIN_GROUP + `/${groupId}`);
export const getGroupData = (groupId, page, size) => apiClient.get(ENDPOINTS.ADMIN_GROUP + `/group-data/${groupId}?page=${page}&size=${size}`, SCREEN);
export const searchStudent = (searchText) => apiClient.get(ENDPOINTS.ADMIN_GROUP + `/search-student?username=${searchText}`);
/**
 * `joinedOn` (`YYYY-MM-DD`) is optional and today by default.
 *
 * It is money, not bookkeeping: the first month's tuition is charged for the
 * lessons remaining on that date, so a student registered a week after they
 * started attending has to be recorded as having joined then.
 */
export const addStudentToGroup = (studentId, groupId, joinedOn) =>
  apiClient.post(ENDPOINTS.ADMIN_GROUP + `/add-student`, null, {
    params: { 'student-id': studentId, 'group-id': groupId, ...(joinedOn ? { 'joined-on': joinedOn } : {}) },
  });

/** Join/leave dates for everyone in a group. */
export const getGroupEnrolments = (groupId) =>
  apiClient.get(ENDPOINTS.ADMIN_GROUP + `/${groupId}/enrolments`);

/** Correct when a student joined — it decides their first month's charge. */
export const updateGroupEnrolment = (groupId, studentId, payload) =>
  apiClient.put(ENDPOINTS.ADMIN_GROUP + `/${groupId}/enrolments/${studentId}`, payload);
export const removeStudentFromGroup = (studentId, groupId) => apiClient.delete(ENDPOINTS.ADMIN_GROUP + `/remove-student/${studentId}?group-id=${groupId}`);
export const getLessonSchedule = (searchText, page, size) => apiClient.get(ENDPOINTS.ADMIN_LESSON + `?keyword=${searchText}&page=${page}&size=${size}`, SCREEN);
export const checkScheduleConflict = (scheduleId, schedule) => apiClient.post(ENDPOINTS.ADMIN_LESSON + "/check-conflict?lesson-schedule-id=" + scheduleId, schedule);
export const createLessonSchedule = (schedule) => apiClient.post(ENDPOINTS.ADMIN_LESSON, schedule);
export const updateLessonSchedule = (scheduleId, schedule) => apiClient.put(ENDPOINTS.ADMIN_LESSON + `/${scheduleId}`, schedule);
export const deleteLessonSchedule = (scheduleId) => apiClient.delete(ENDPOINTS.ADMIN_LESSON + `/${scheduleId}`);
export const getStudentList = (searchText, page, size) => apiClient.get(ENDPOINTS.ADMIN_USER + `?role=STUDENT&searching=${searchText}&page=${page}&size=${size}`, SCREEN)
export const getGroupsOfStudent = (studentId) => apiClient.get(ENDPOINTS.ADMIN_GROUP + `/groups-of-student/${studentId}`, SCREEN);
export const getTeacherList = (searchText, page, size) => apiClient.get(ENDPOINTS.ADMIN_USER+`?role=TEACHER&searching=${searchText}&page=${page}&size=${size}`, SCREEN)
export const getGroupsOfTeacher = (teacherId) => apiClient.get(ENDPOINTS.ADMIN_GROUP + `/groups-of-teacher/${teacherId}`, SCREEN)
export const getGroupIdAndName = () => apiClient.get(ENDPOINTS.ADMIN_GROUP + "/group-id-and-name", REFERENCE)
export const addUser = (user) => apiClient.post(ENDPOINTS.ADMIN_USER, user);
// Non-blocking hint: how many accounts already use a phone (siblings may share one).
export const getPhoneUsage = (phone) => apiClient.get(ENDPOINTS.ADMIN_USER + `/phone-usage?phone=${encodeURIComponent(phone)}`);
export const editUser = (user, userId) => apiClient.put(ENDPOINTS.ADMIN_USER + `/${userId}`, user);
export const deleteUser = (id) => apiClient.delete(ENDPOINTS.ADMIN_USER + `/${id}`);
export const setUserActive = (id, active) => apiClient.patch(ENDPOINTS.ADMIN_USER + `/${id}/active?active=${active}`);
export const getUpdates = () => apiClient.get(ENDPOINTS.ADMIN_UPDATE, SCREEN)
export const createUpdate = (update) => apiClient.post(ENDPOINTS.ADMIN_UPDATE, update)
export const updateUpdate = (updateId, update) => apiClient.put(ENDPOINTS.ADMIN_UPDATE + `/${updateId}`, update)
export const deleteUpdate = (updateId) => apiClient.delete(ENDPOINTS.ADMIN_UPDATE + `/${updateId}`)
export const getRoomList = () => apiClient.get(ENDPOINTS.ADMIN_ROOM, REFERENCE);
export const createRoom = (roomData) => apiClient.post(ENDPOINTS.ADMIN_ROOM, roomData);
export const updateRoom = (roomId, roomData) => apiClient.put(ENDPOINTS.ADMIN_ROOM + `/${roomId}`, roomData);
export const deleteRoom = (roomId) => apiClient.delete(ENDPOINTS.ADMIN_ROOM + `/${roomId}`);

// ======================================= TEACHER ==========================================

export const getTeacherGroups = (searchText, page, size) =>
  apiClient.get(ENDPOINTS.TEACHER_GROUP + `?keyword=${searchText}&page=${page}&size=${size}`, SCREEN);
export const getTeacherGroupStudents = (groupId) => apiClient.get(ENDPOINTS.TEACHER_GROUP.replace('/group', `/group/${groupId}/students`), SCREEN);
export const getTeacherLessons = () => apiClient.get(ENDPOINTS.TEACHER_LESSON);
export const getTeacherWeeklySchedule = (weekStartDate) => apiClient.get(ENDPOINTS.TEACHER_LESSONS + `/weekly-schedule?weekStartDate=${weekStartDate}`, SCREEN);
export const getTeacherTaskList = (groupId) => apiClient.get(ENDPOINTS.TEACHER_TASK + `?group-id=${groupId}`, SCREEN)
export const createTeacherTask = (taskData) => apiClient.post(ENDPOINTS.TEACHER_TASK, taskData);
export const updateTeacherTask = (taskId, taskData) => apiClient.put(ENDPOINTS.TEACHER_TASK + `/${taskId}`, taskData, { headers: { "Content-Type": "multipart/form-data" } });
export const deleteTeacherTask = (taskId) => apiClient.delete(ENDPOINTS.TEACHER_TASK + `/${taskId}`);
export const getTeacherHomeworkList = (taskId, groupId, page, size) => apiClient.get(ENDPOINTS.TEACHER_HOMEWORK + `?task-id=${taskId}&group-id=${groupId}&page=${page}&size=${size}`);
export const evaluateHomework = (homeworkId, data) => apiClient.patch(ENDPOINTS.TEACHER_HOMEWORK + `/${homeworkId}`, data);
export const getTeacherCourseIds = () => apiClient.get(ENDPOINTS.TEACHER_COURSE_ID, REFERENCE)

// ====================================== STUDENT ===========================================

export const getStudentDashboard = () => apiClient.get(ENDPOINTS.STUDENT_DASHBOARD, SCREEN);
export const getStudentHomeworkNotification = () => apiClient.get(ENDPOINTS.STUDENT_HOMEWORK_NOTIFICATION);
export const getStudentSubjectList = (searchText, page, size) =>
  apiClient.get(ENDPOINTS.STUDENT_GROUP + `?keyword=${searchText}&page=${page}&size=${size}`, SCREEN);
export const getStudentLessons = () => apiClient.get(ENDPOINTS.STUDENT_LESSON)
export const getStudentWeeklySchedule = (weekStartDate) => apiClient.get(ENDPOINTS.STUDENT_LESSONS + `/weekly-schedule?weekStartDate=${weekStartDate}`, SCREEN);
export const getStudentHomework = (groupId) => apiClient.get(ENDPOINTS.STUDENT_HOMEWORK + `?group-id=${groupId}`, SCREEN);
export const uploadHomework = (taskId, homework) => apiClient.post(ENDPOINTS.STUDENT_HOMEWORK + `?task-id=${taskId}`, homework);
export const reUploadHomework = (homeworkId, taskId, homework) => apiClient.patch(ENDPOINTS.STUDENT_HOMEWORK + `/${homeworkId}?task-id=${taskId}`, homework);

// ======================================= SHARED ===========================================

export const getUserUpdates = (role) => apiClient.get(role === 'TEACHER' ? ENDPOINTS.TEACHER_DASHBOARD : ENDPOINTS.STUDENT_DASHBOARD, SCREEN)
export const download = (groupId, fileId) => apiClient.get(ENDPOINTS.DOWNLOAD + `/${groupId}?file-id=${fileId}`,{ responseType: "blob" })
export const getUserInfo = () => apiClient.get(ENDPOINTS.USERINFO, PROFILE);
export const getMyActivity = (page = 0, size = 20) =>
  apiClient.get(`${ENDPOINTS.MY_ACTIVITY}?page=${page}&size=${size}`);

// ====================================== MESSAGES ==========================================

export const getConversations = () => apiClient.get(ENDPOINTS.CONVERSATION);
// Smart message loading: fetch one small page at a time (newest first) instead of
// downloading the whole conversation. Older pages are loaded lazily as the user
// scrolls up. Keep this in sync with the page-size assumptions in ChatInterface.
export const MESSAGE_PAGE_SIZE = 30;
export const getMessages = (conversationId, page = 0, size = MESSAGE_PAGE_SIZE) =>
    apiClient.get(`${ENDPOINTS.CONVERSATION}/${conversationId}/message?page=${page}&size=${size}&sort=createdAt,desc`);
export const createConversation = (data) => apiClient.post(ENDPOINTS.CONVERSATION, data)
export const searchConversations = (query, searchType) => apiClient.get(ENDPOINTS.CONVERSATION + `/search?term=${query}&type=${searchType}`)
export const joinGroupMessages = (id) => apiClient.post(ENDPOINTS.CONVERSATION + `/conversation/${id}/join`)
export const getConversationParticipants = (conversationId) => apiClient.get(ENDPOINTS.CONVERSATION + `/${conversationId}/participants`, PROFILE);
export const getUnreadCount = () => apiClient.get(ENDPOINTS.MESSAGES_UNREAD_COUNT);
export const markAsRead = (messageId) => apiClient.put(ENDPOINTS.MESSAGES + `/${messageId}/read`);

export const uploadVoiceMessage = (conversationId, blob, durationSeconds) => {
  const formData = new FormData();
  const extensionFromMime = (blob.type || '').includes('mp4') ? 'm4a'
    : (blob.type || '').includes('ogg') ? 'ogg'
    : 'webm';
  const filename = `voice-${Date.now()}.${extensionFromMime}`;
  formData.append('file', blob, filename);
  formData.append('conversationId', conversationId);
  formData.append('durationSeconds', String(durationSeconds));
  return apiClient.post(ENDPOINTS.CONVERSATION + '/voice-message', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const downloadVoiceMessage = (fileId) =>
  apiClient.get(ENDPOINTS.DOWNLOAD + `/voice-message/${fileId}`, { responseType: 'blob' });

export const uploadMessageAttachment = (conversationId, file) => {
  const formData = new FormData();
  formData.append('file', file, file.name || `attachment-${Date.now()}`);
  formData.append('conversationId', conversationId);
  return apiClient.post(ENDPOINTS.CONVERSATION + '/attachment', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const downloadMessageAttachment = (fileId) =>
  apiClient.get(ENDPOINTS.DOWNLOAD + `/attachment/${fileId}`, { responseType: 'blob' });

// ====================================== PUBLIC PROFILE ==========================================

export const getPublicProfile = (userId) => apiClient.get(ENDPOINTS.PROFILE_BY_ID(userId), PROFILE);
export const getPublicProfileByUsername = (username) => apiClient.get(ENDPOINTS.PROFILE_BY_USERNAME(username), PROFILE);
export const getMyPublicProfile = () => apiClient.get(ENDPOINTS.PROFILE + "/me", PROFILE);
export const updateBio = (bio) => apiClient.put(ENDPOINTS.PROFILE_BIO, { bio });
export const uploadProfilePhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(ENDPOINTS.PROFILE_PHOTO, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const removeProfilePhoto = () => apiClient.delete(ENDPOINTS.PROFILE_PHOTO);
export const updateUsername = (username) => apiClient.patch(ENDPOINTS.PROFILE + '/username', { username });
export const updatePreferredLanguage = (language) => apiClient.patch(ENDPOINTS.PROFILE + '/language', { language });

// ====================================== PERMISSIONS ==========================================

export const getAllPermissions = () => apiClient.get('/admin/permissions', REFERENCE);
export const getRolePermissions = (role) => apiClient.get(`/admin/permissions/role/${role}`);
export const getUserEffectivePermissions = (userId) => apiClient.get(`/admin/permissions/user/${userId}`);
export const getUserPermissionOverrides = (userId) => apiClient.get(`/admin/permissions/user/${userId}/overrides`);
export const grantPermission = (userId, data) => apiClient.post(`/admin/permissions/user/${userId}/grant`, data);
export const revokePermission = (userId, data) => apiClient.post(`/admin/permissions/user/${userId}/revoke`, data);
export const removePermissionOverride = (userId, permission) => apiClient.delete(`/admin/permissions/user/${userId}/override/${permission}`);
export const updateUserPermissions = (userId, data) => apiClient.put(`/admin/permissions/user/${userId}`, data);
export const isUserReadOnly = (userId) => apiClient.get(`/admin/permissions/user/${userId}/read-only`);
export const setUserReadOnlyMode = (userId, enabled, reason) => apiClient.post(`/admin/permissions/user/${userId}/read-only?enabled=${enabled}&reason=${reason || ''}`);

// ====================================== QUESTION BANK ==========================================

export const getQuestionBank = (params) => apiClient.get('/question-bank', { params, ...SCREEN });
export const getQuestionById = (id) => apiClient.get(`/question-bank/${id}`);
export const createQuestion = (data) => apiClient.post('/question-bank', data);
export const updateQuestion = (id, data) => apiClient.put(`/question-bank/${id}`, data);
export const deleteQuestion = (id) => apiClient.delete(`/question-bank/${id}`);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const throwJobError = (errMsg, flag) => {
  const err = new Error(errMsg || 'AI parsing failed');
  err[flag] = true;
  throw err;
};

// Drive an AI parse job to completion. Both parse entry points below (documents
// and photos) return the same job handle, and both need the same "poll until
// done" loop — the backend does the work on a background thread so no single
// HTTP request outlives a reverse-proxy read timeout (~60s).
//
// Resolves to an axios-like shape { data: { success, data: { questions, usedMode, images, rawCount } } }
// so callers keep working. Throws on FAILED/timeout with a flagged Error.
//
// `onProgress(elapsedSeconds)` is called once per poll so the UI can show progress.
const awaitParseJob = async (startResp, onProgress) => {
  const job = startResp?.data?.data;
  if (!startResp?.data?.success || !job) {
    return startResp; // let the caller handle an unexpected shape
  }

  const finishedToResponse = (result) => ({ data: { success: true, data: result } });

  if (job.status === 'COMPLETED') {
    return finishedToResponse(job.result);
  }
  if (job.status === 'FAILED') {
    throwJobError(job.error, 'aiParseFailed');
  }

  // PROCESSING — poll the job endpoint.
  const jobId = job.jobId;
  const pollIntervalMs = 3000;
  const maxAttempts = 80; // ~4 minutes
  const startedAt = Date.now();
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await sleep(pollIntervalMs);
    if (onProgress) onProgress(Math.round((Date.now() - startedAt) / 1000));

    let pollResp;
    try {
      pollResp = await apiClient.get(`/question-bank/parse-ai/jobs/${jobId}`, { timeout: 30000 });
    } catch (pollError) {
      // Transient network blip while polling — keep trying until maxAttempts.
      if (pollError?.statusCode === 404) {
        throwJobError('AI parse job expired, please re-upload', 'aiParseFailed');
      }
      continue;
    }

    const pj = pollResp?.data?.data;
    if (!pollResp?.data?.success || !pj) continue;
    if (pj.status === 'COMPLETED') {
      return finishedToResponse(pj.result);
    }
    if (pj.status === 'FAILED') {
      throwJobError(pj.error, 'aiParseFailed');
    }
  }

  return throwJobError('AI parsing timed out', 'aiParseTimeout');
};

const parseContextParams = ({ courseId, topic, subtopic }) => {
  const params = new URLSearchParams();
  if (courseId != null) params.set('courseId', String(courseId));
  if (topic) params.set('topic', topic);
  if (subtopic) params.set('subtopic', subtopic);
  return params;
};

// AI-aware parse of a document that auto-detects images: text-only files come
// back from the local parser (no AI billed), image-bearing ones go to vision.
export const parseQuestionsFileAi = async (
  file,
  { courseId, topic, subtopic, mode = 'AUTO', onProgress } = {}
) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = parseContextParams({ courseId, topic, subtopic });
  if (mode) params.set('mode', mode);
  const qs = params.toString();

  // Kick off the job (fast — returns a job handle, not the result).
  const startResp = await apiClient.post(
    `/question-bank/parse-ai${qs ? `?${qs}` : ''}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
  );

  return awaitParseJob(startResp, onProgress);
};

// Parse questions out of pasted text. Usually free and instant: the backend
// runs its regex parser first and only escalates to the AI when the text is not
// in the standard numbered format (or carries LaTeX, which the regex parser
// mis-splits). `mode` is 'AUTO' | 'AI' | 'LOCAL'.
export const parseQuestionsFromText = async (text, { mode = 'AUTO', onProgress } = {}) => {
  const startResp = await apiClient.post(
    '/question-bank/parse-text',
    { text, mode },
    { timeout: 60000 }
  );

  return awaitParseJob(startResp, onProgress);
};

// Parse questions straight from photographed pages. `files` order is
// significant — the backend treats them as consecutive pages of one document,
// so a question spanning two photos is stitched back together.
export const parseQuestionsFromImages = async (
  files,
  { courseId, topic, subtopic, onProgress } = {}
) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const qs = parseContextParams({ courseId, topic, subtopic }).toString();

  const startResp = await apiClient.post(
    `/question-bank/parse-images${qs ? `?${qs}` : ''}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
  );

  return awaitParseJob(startResp, onProgress);
};

export const parseQuestionsFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post('/question-bank/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const importQuestions = (courseId, topic, fileName, questions) =>
  apiClient.post(`/question-bank/import?courseId=${courseId}&topic=${topic || ''}&fileName=${fileName}`, questions);
export const getQuestionStats = (courseId) => apiClient.get(`/question-bank/stats/${courseId}`, SCREEN);
export const uploadQuestionImage = (file, courseId, topic = null, subtopic = null) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = new URLSearchParams();
  params.append('courseId', courseId);
  if (topic) params.append('topic', topic);
  if (subtopic) params.append('subtopic', subtopic);
  return apiClient.post(`/question-bank/upload-image?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// ====================================== QUESTION BANK HIERARCHY ==========================================

// Topics (sections within a course)
export const getTopicsForCourse = (courseId) =>
  apiClient.get(`/question-bank/hierarchy/courses/${courseId}/topics`, SCREEN);
export const createTopic = (courseId, name, description) =>
  apiClient.post(`/question-bank/hierarchy/courses/${courseId}/topics`, { name, description });
export const updateTopic = (topicId, name, description) =>
  apiClient.put(`/question-bank/hierarchy/topics/${topicId}`, { name, description });
export const deleteTopic = (topicId) =>
  apiClient.delete(`/question-bank/hierarchy/topics/${topicId}`);

// Whole course tree (topics → subtopics → sets) in one call. Used by the
// "copy questions to another set" picker, which searches across the course
// instead of expanding one folder at a time.
export const getQuestionBankTree = (courseId) =>
  apiClient.get(`/question-bank/hierarchy/courses/${courseId}/tree`, SCREEN);

// SubTopics (subsections within a topic)
export const getSubTopicsForTopic = (topicId) =>
  apiClient.get(`/question-bank/hierarchy/topics/${topicId}/subtopics`, SCREEN);
export const createSubTopic = (topicId, name, description) =>
  apiClient.post(`/question-bank/hierarchy/topics/${topicId}/subtopics`, { name, description });
export const updateSubTopic = (subTopicId, name, description) =>
  apiClient.put(`/question-bank/hierarchy/subtopics/${subTopicId}`, { name, description });
export const deleteSubTopic = (subTopicId) =>
  apiClient.delete(`/question-bank/hierarchy/subtopics/${subTopicId}`);

// ====================================== QUESTION SETS ==========================================

export const getQuestionSets = (params) => apiClient.get('/question-sets', { params, ...SCREEN });
export const getQuestionSetsForSubTopic = (subTopicId) =>
  apiClient.get(`/question-sets/subtopics/${subTopicId}`, SCREEN);
export const getQuestionSetById = (id, includeQuestions = true) =>
  apiClient.get(`/question-sets/${id}?includeQuestions=${includeQuestions}`, SCREEN);
export const createQuestionSet = (subTopicId, data) =>
  apiClient.post(`/question-sets/subtopics/${subTopicId}`, data);
export const updateQuestionSet = (id, data) => apiClient.put(`/question-sets/${id}`, data);
export const deleteQuestionSet = (id) => apiClient.delete(`/question-sets/${id}`);
export const importQuestionSetFromFile = (file, subTopicId, name) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  return apiClient.post(`/question-sets/subtopics/${subTopicId}/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const importParsedQuestions = (subTopicId, name, sourceFile, questions) =>
  apiClient.post(`/question-sets/subtopics/${subTopicId}/import-parsed`, {
    name,
    sourceFile,
    questions
  });
// Legacy endpoints (for backward compatibility)
export const getQuestionSetTopics = (courseId) => apiClient.get(`/question-sets/topics/${courseId}`);
export const getQuestionSetSubTopics = (courseId, topic) =>
  apiClient.get(`/question-sets/legacy/subtopics/${courseId}?topic=${encodeURIComponent(topic)}`);
export const submitQuestionSetAnswers = (setId, answers) =>
  apiClient.post(`/question-sets/${setId}/answers`, answers);
export const addQuestionToSet = (setId, questionData) =>
  apiClient.post(`/question-sets/${setId}/questions`, questionData);
export const removeQuestionFromSet = (setId, questionId) =>
  apiClient.delete(`/question-sets/${setId}/questions/${questionId}`);
export const reorderQuestionsInSet = (setId, items) =>
  apiClient.put(`/question-sets/${setId}/reorder`, items);

/**
 * Copy questions out of one set into any number of destination sets, and/or
 * into a new set created in `newSetSubTopicId`. Server-side deep copy: each
 * destination gets its own question rows.
 */
export const copyQuestionsToSets = ({
  sourceSetId, questionIds, targetSetIds = [], newSetSubTopicId = null,
  newSetName = null, skipDuplicates = true,
}) =>
  apiClient.post('/question-sets/copy-questions', {
    sourceSetId, questionIds, targetSetIds, newSetSubTopicId, newSetName, skipDuplicates,
  });

// AI generation
export const aiGenerateSetPreview = (subTopicId, body) =>
  apiClient.post(`/question-sets/subtopics/${subTopicId}/ai-generate-preview`, body);
export const aiGenerateAndCreateSet = (subTopicId, body) =>
  apiClient.post(`/question-sets/subtopics/${subTopicId}/ai-generate`, body);
export const aiAddMoreToSet = (setId, body) =>
  apiClient.post(`/question-sets/${setId}/ai-add-more`, body);

// ====================================== PRACTICE TESTS ==========================================

export const getTeacherPracticeTests = (page = 0, size = 20) =>
  apiClient.get(`/practice-tests/teacher?page=${page}&size=${size}`, SCREEN);
export const getTeacherPracticeTestNotifications = () =>
  apiClient.get('/practice-tests/teacher/notifications');
export const getStudentPracticeTests = (groupIds) =>
  apiClient.get('/practice-tests/student', { params: { groupIds: groupIds.join(',') }, ...SCREEN });
export const getPracticeTestById = (id, includeQuestions = true) =>
  apiClient.get(`/practice-tests/${id}?includeQuestions=${includeQuestions}`);
export const createPracticeTest = (data) => apiClient.post('/practice-tests', data);
export const deletePracticeTest = (id) => apiClient.delete(`/practice-tests/${id}`);
export const updatePracticeTestTitle = (id, title) =>
  apiClient.patch(`/practice-tests/${id}/title`, null, { params: { title } });
export const updatePracticeTestDescription = (id, description) =>
  apiClient.patch(`/practice-tests/${id}/description`, null, { params: { description } });
export const updatePracticeTestStatus = (id, status) =>
  apiClient.patch(`/practice-tests/${id}/status?status=${status}`);
export const submitPracticeTestAnswers = (id, answers) =>
  apiClient.post(`/practice-tests/${id}/answers`, answers);
export const getPracticeTestSubmissions = (id) =>
  apiClient.get(`/practice-tests/${id}/submissions`);
export const getPracticeTestLeaderboard = (id) =>
  apiClient.get(`/practice-tests/${id}/leaderboard`);
export const startPracticeTest = (id) => apiClient.post(`/practice-tests/${id}/start`);
export const submitPracticeTest = (id, answers) =>
  apiClient.post(`/practice-tests/${id}/submit`, answers);
export const getMyPracticeTestSubmission = (id) =>
  apiClient.get(`/practice-tests/${id}/my-submission`);
/**
 * Record scores for a test the class sat on paper. `scores` is
 * `[{ studentId, correctCount }]` — a total per student, no per-question
 * detail. Rejected as a whole if any row is out of range; the server never
 * saves half a batch.
 */
export const recordPracticeTestScores = (id, scores, overwrite = false) =>
  apiClient.post(`/practice-tests/${id}/scores`, { scores, overwrite });
/**
 * Report a proctoring violation. The server owns the count and the verdict —
 * the response says whether to warn or whether the run has been ended.
 * `answers` is the client's current work, graded only if this report
 * terminates the run.
 */
export const reportPracticeTestViolation = (id, report) =>
  apiClient.post(`/practice-tests/${id}/violations`, report);

// ====================================== ANALYTICS ==========================================

export const getTeacherDashboardAnalytics = () =>
  apiClient.get('/analytics/teacher/dashboard', SCREEN);
export const getGroupAnalytics = (groupId) =>
  apiClient.get(`/analytics/groups/${groupId}`, SCREEN);
export const getStudentPerformance = (studentId) =>
  apiClient.get(`/analytics/students/${studentId}`, SCREEN);
export const getMyPerformance = () =>
  apiClient.get('/analytics/my-performance', SCREEN);
export const getQuestionAnalytics = (testId) =>
  apiClient.get(`/analytics/tests/${testId}/questions`, SCREEN);

// ====================================== LESSON INSTANCES (TEACHER) ==========================================

export const getTodaysLessonInstances = () =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/today`);
/**
 * The lesson under way and the ones either side of it — `{previous, current,
 * next, afterNext}`, any slot nullable. The phone's home screen already leads
 * with this; the web dashboard's focus card reads the same answer, so the two
 * cannot disagree about where the teacher is meant to be.
 */
export const getTeacherNowNextLesson = () =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/now-next`);
export const getTeacherLessonInstancesInRange = (startDate, endDate) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/range?startDate=${startDate}&endDate=${endDate}`, SCREEN);
export const getLessonInstancesByGroup = (groupId) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/group/${groupId}`, SCREEN);
export const getLessonInstancesByGroupAndRange = (groupId, startDate, endDate) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/group/${groupId}/range?startDate=${startDate}&endDate=${endDate}`, SCREEN);
export const getNextLessonDate = (groupId) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/group/${groupId}/next-date`);
export const getLessonInstanceById = (instanceId) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}`);
export const createLessonInstance = (groupId, data) =>
  apiClient.post(`${ENDPOINTS.TEACHER_LESSONS}/group/${groupId}`, data);
export const updateLessonInstance = (instanceId, data) =>
  apiClient.put(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}`, data);
export const cancelLessonInstance = (instanceId, reason) =>
  apiClient.post(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/cancel`, { reason });
export const rescheduleLessonInstance = (instanceId) =>
  apiClient.post(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/reschedule`);
export const completeLessonInstance = (instanceId) =>
  apiClient.post(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/complete`);
export const deleteLessonInstance = (instanceId) =>
  apiClient.delete(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}`);
export const insertLessonInstance = (groupId, insertBeforeInstanceId, data) =>
  apiClient.post(`${ENDPOINTS.TEACHER_LESSONS}/group/${groupId}/insert${insertBeforeInstanceId ? `?insertBeforeInstanceId=${insertBeforeInstanceId}` : ''}`, data);
export const getStudentsForAttendance = (instanceId) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/students`);
export const canMarkAttendance = (instanceId) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/can-mark-attendance`);
// The teacher's half of marking a class: who brought the homework set last lesson.
// {instanceId} is the lesson being taught; the marks land on the previous lesson.
// Attendance (present/late) is reception's job, at /attendance.
export const markHomework = (instanceId, entries) =>
  apiClient.post(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/homework`, { entries });
/**
 * Lessons this teacher still owes a homework mark on — the dashboard's backlog.
 *
 * Each row carries two lesson ids: `lessonInstanceId` is where the homework was
 * set and where the marks are stored, `markAtLessonInstanceId` is the lesson
 * whose drawer records them (the next class). Navigate to the second.
 */
export const getHomeworkBacklog = (limit) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/homework-backlog`, {
    params: limit ? { limit } : undefined,
  });
export const getPendingAttendanceLessons = (groupId) =>
  apiClient.get(`${ENDPOINTS.TEACHER_LESSONS}/group/${groupId}/pending-attendance`);
export const uploadLessonMaterial = (instanceId, file, title, description) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  if (description) formData.append('description', description);
  return apiClient.post(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/materials`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteLessonMaterial = (instanceId, materialId) =>
  apiClient.delete(`${ENDPOINTS.TEACHER_LESSONS}/${instanceId}/materials/${materialId}`);
export const downloadLessonMaterialFile = (groupId, fileId) =>
  apiClient.get(`${ENDPOINTS.DOWNLOAD}/${groupId}?file-id=${encodeURIComponent(fileId)}`, {
    responseType: 'blob'
  });

// ====================================== ATTENDANCE ==========================================

export const markAttendance = (data) =>
  apiClient.post(ENDPOINTS.TEACHER_ATTENDANCE, data);
export const getAttendanceByLesson = (lessonInstanceId) =>
  apiClient.get(`${ENDPOINTS.TEACHER_ATTENDANCE}/lesson/${lessonInstanceId}`);

// Role-neutral attendance API. Admins mark here — /teacher/** is gated on
// ROLE_TEACHER, so reception cannot use the calls above at all.
export const getAttendanceBoard = (date) =>
  apiClient.get(ENDPOINTS.ATTENDANCE_BOARD, { params: date ? { date } : undefined });
export const getAttendanceRoster = (lessonInstanceId) =>
  apiClient.get(ENDPOINTS.ATTENDANCE_ROSTER(lessonInstanceId));
/** Whole roster in one call; safe to retry with the same payload. */
export const markAttendanceRoster = (lessonInstanceId, entries) =>
  apiClient.post(ENDPOINTS.ATTENDANCE_LESSON(lessonInstanceId), { entries });

// ====================================== TUITION ===========================================

// Money in a small learning centre is cash over the desk. The split below is the
// anti-fraud control, not an accident of naming: any admin may call
// recordTuitionPayment; only a user granted FINANCE_ADJUST may call the rest.
// The server refuses either way — hiding the buttons is courtesy, not security.

/**
 * Charged, collected, outstanding, debtor count and collection rate for one month.
 *
 * `period` is `YYYY-MM` and defaults server-side to the current month. Every
 * figure in the response belongs to it — the response echoes the period back so
 * a screen can never label last month's numbers as this month's.
 */
export const getTuitionSummary = (period) =>
  apiClient.get(ENDPOINTS.TUITION_SUMMARY, { params: period ? { period } : {} });

/**
 * One page of the tuition list.
 *
 * `filter` is DEBTORS (default) | NO_DEBT | ALL, and `query` matches part of a
 * name, a username or either phone number — a paid-up student is otherwise
 * unreachable, since the only route to a statement is a row on this list.
 *
 * `sort` is AMOUNT | DAYS_OVERDUE | NAME. `totalElements` is what the filter and
 * search matched, for the paginator; `debtorCount` and `totalDebt` describe the
 * whole centre, so the headline does not change while somebody is searching.
 *
 * `period` (`YYYY-MM`) asks who owed money at the end of that month, which is a
 * different question from who owes now and the one an owner reconciling a
 * closed month is asking.
 */
export const getDebtors = ({
  sort = 'AMOUNT',
  filter = 'DEBTORS',
  query = '',
  page = 0,
  size = 20,
  period,
} = {}) =>
  apiClient.get(ENDPOINTS.TUITION_DEBTORS, {
    params: { sort, filter, page, size, ...(query ? { query } : {}), ...(period ? { period } : {}) },
  });

/**
 * Students to take money from — any of them, not just debtors.
 *
 * A blank query returns the first page of active students. Nothing on the web
 * calls this since the tuition list grew its own search and a Pay button on every
 * row; it backs `SelectStudentModal`, which is kept for the same reason the
 * endpoint is — the Android client's picker still uses it.
 */
export const findTuitionStudents = (query, limit = 20) =>
  apiClient.get(ENDPOINTS.TUITION_STUDENTS, { params: { query: query || '', limit } });

/**
 * Whether billing is running for a month, and what is stopping it — including
 * `firstBillableMonth`, the floor of the month picker.
 */
export const getTuitionReadiness = (period) =>
  apiClient.get(ENDPOINTS.TUITION_READINESS, { params: period ? { period } : {} });

export const getStudentStatement = (studentId, page = 0, size = 20, period) =>
  apiClient.get(ENDPOINTS.TUITION_STATEMENT(studentId), {
    params: { page, size, ...(period ? { period } : {}) },
  });

/** A student's own statement — the only tuition call a student may make. */
export const getMyStatement = (page = 0, size = 20, period) =>
  apiClient.get(ENDPOINTS.TUITION_MY_STATEMENT, {
    params: { page, size, ...(period ? { period } : {}) },
  });

/** Amount is a positive magnitude; the server applies the ledger's sign. */
export const recordTuitionPayment = (payload) => apiClient.post(ENDPOINTS.TUITION_PAYMENTS, payload);

/**
 * Upload the parent's receipt for a card transfer; returns the file to reference.
 *
 * Separate from recording the payment so the slow part (a photo from a phone)
 * happens while the amount is still being typed.
 */
export const uploadTuitionReceipt = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(ENDPOINTS.TUITION_RECEIPTS, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/** The receipt behind a payment row. Staff only; comes back as a blob. */
export const downloadTuitionReceipt = (fileId) =>
  apiClient.get(ENDPOINTS.TUITION_RECEIPT(fileId), { responseType: 'blob' });

/** DISCOUNT | WAIVER | ADJUSTMENT | REFUND. Needs FINANCE_ADJUST. */
export const recordTuitionEntry = (payload) => apiClient.post(ENDPOINTS.TUITION_ENTRIES, payload);

/** Cancels an entry by writing its opposite; both rows stay on the statement. */
export const reverseTuitionEntry = (entryId, reason) =>
  apiClient.post(ENDPOINTS.TUITION_REVERSE(entryId), { reason });

export const setStudentTuitionOverride = (studentId, payload) =>
  apiClient.put(ENDPOINTS.TUITION_OVERRIDE(studentId), payload);
export const clearStudentTuitionOverride = (studentId) =>
  apiClient.delete(ENDPOINTS.TUITION_OVERRIDE(studentId));


export const getTuitionSettings = () => apiClient.get(ENDPOINTS.TUITION_SETTINGS);
export const updateTuitionSettings = (payload) => apiClient.put(ENDPOINTS.TUITION_SETTINGS, payload);

/** Safe to press twice: a re-run only charges students who were missed. */
export const runTuitionAccrual = (period) =>
  apiClient.post(ENDPOINTS.TUITION_ACCRUAL, null, { params: { period } });

// ====================================== RANKINGS ==========================================

// Group ranking (practice test based)
export const getGroupRanking = (groupId) =>
  apiClient.get(ENDPOINTS.RANKING_GROUP(groupId), SCREEN);
export const getMyGroupRank = (groupId) =>
  apiClient.get(ENDPOINTS.RANKING_GROUP_MY_RANK(groupId), SCREEN);

// Mock exam ranking (system-wide)
export const getMockExamSessions = () =>
  apiClient.get(ENDPOINTS.RANKING_MOCK_EXAM_SESSIONS, SCREEN);
export const getMockExamRanking = (sessionId) =>
  apiClient.get(ENDPOINTS.RANKING_MOCK_EXAM(sessionId), SCREEN);
export const getLatestMockExamRanking = () =>
  apiClient.get(ENDPOINTS.RANKING_MOCK_EXAM_LATEST, SCREEN);
export const getMyMockExamRank = (sessionId) =>
  apiClient.get(ENDPOINTS.RANKING_MOCK_EXAM_MY_RANK(sessionId), SCREEN);
export const getMyMockExamResults = () =>
  apiClient.get(ENDPOINTS.RANKING_MOCK_EXAM_MY_RESULTS, SCREEN);
export const getSubjectPairs = () =>
  apiClient.get(ENDPOINTS.RANKING_SUBJECT_PAIRS, REFERENCE);

// Admin mock exam management
export const createMockExamSession = (data) =>
  apiClient.post(ENDPOINTS.ADMIN_MOCK_EXAM_SESSIONS, data);
export const getAllMockExamSessions = () =>
  apiClient.get(ENDPOINTS.ADMIN_MOCK_EXAM_SESSIONS, SCREEN);
export const getMockExamSession = (sessionId) =>
  apiClient.get(ENDPOINTS.ADMIN_MOCK_EXAM_SESSION(sessionId), SCREEN);
export const updateMockExamSession = (sessionId, data) =>
  apiClient.put(ENDPOINTS.ADMIN_MOCK_EXAM_SESSION(sessionId), data);
export const deleteMockExamSession = (sessionId) =>
  apiClient.delete(ENDPOINTS.ADMIN_MOCK_EXAM_SESSION(sessionId));
export const enterMockExamResult = (data) =>
  apiClient.post(ENDPOINTS.ADMIN_MOCK_EXAM_RESULTS, data);
export const getMockExamSessionResults = (sessionId) =>
  apiClient.get(ENDPOINTS.ADMIN_MOCK_EXAM_SESSION_RESULTS(sessionId), SCREEN);
export const deleteMockExamResult = (resultId) =>
  apiClient.delete(ENDPOINTS.ADMIN_MOCK_EXAM_RESULT(resultId));
export const searchStudentsForMockExam = (query) =>
  apiClient.get(ENDPOINTS.ADMIN_MOCK_EXAM_SEARCH_STUDENTS, { params: { query } });
export const getStudentsWithResultStatus = (sessionId, search, hasResult) => {
  const params = {};
  if (search) params.search = search;
  if (hasResult !== null && hasResult !== undefined && hasResult !== 'all') {
    params.hasResult = hasResult === 'true' || hasResult === true;
  }
  return apiClient.get(ENDPOINTS.ADMIN_MOCK_EXAM_SESSION_STUDENTS(sessionId), { params });
};

// ====================================== LESSON INSTANCES (STUDENT) ==========================================

export const getUpcomingLessons = () =>
  apiClient.get(`${ENDPOINTS.STUDENT_LESSONS}/upcoming`, SCREEN);
export const getStudentLessonInstancesByGroup = (groupId) =>
  apiClient.get(`${ENDPOINTS.STUDENT_LESSONS}/group/${groupId}`, SCREEN);
export const getStudentLessonInstanceById = (instanceId) =>
  apiClient.get(`${ENDPOINTS.STUDENT_LESSONS}/${instanceId}`);

/**
 * The lesson under way and the ones either side of it — `{previous, current,
 * next, afterNext}`, any slot nullable.
 *
 * `previous` carries the homework that was set at it, which is the question a
 * student actually opens the app with: *what was I meant to bring today*. The
 * phone's home screen already leads with this endpoint.
 */
export const getStudentNowNextLesson = () =>
  apiClient.get(`${ENDPOINTS.STUDENT_LESSONS}/now-next`);

// ====================================== STUDENT INSIGHTS / WORK =============================

/** Weekly attendance, plus the window's overall percent and its direction. */
export const getStudentAttendanceTrend = (weeks = 8) =>
  apiClient.get(`student/insights/attendance-trend?weeks=${weeks}`);

/**
 * Overdue work and work waiting to be marked. The bell's
 * `getStudentHomeworkNotification()` answers the third state — due soon — and
 * the two lists never overlap.
 */
export const getStudentOutstandingWork = (limit) =>
  apiClient.get(`${ENDPOINTS.STUDENT_HOMEWORK}/outstanding`, {
    params: limit ? { limit } : undefined,
  });

// ====================================== PARENT TELEGRAM LINKS ===============================

// Parents at these centres never come in — the child registers alone and writes
// down a number. So an invitation is issued against the *student* and travels:
// the server sends an SMS, or the admin forwards the link from their own
// Telegram. There is no third channel and nothing to print or scan.

/** `filter` is ALL | LINKED | UNLINKED | INVITED_NEVER_LINKED | NO_PHONE. */
export const getParentLinkBoard = (filter = 'ALL') =>
  apiClient.get(ENDPOINTS.PARENT_LINKS, { params: { filter } });

/** `channel` is SMS | TELEGRAM_MANUAL; `language` is uz-Latn | uz-Cyrl | ru. */
export const inviteParent = (studentId, channel, language) =>
  apiClient.post(ENDPOINTS.PARENT_LINK_INVITE(studentId), { channel, language });

/** Blank clears the number, which is the right record for "that was the student's own". */
export const updateStudentParentPhone = (studentId, parentPhone) =>
  apiClient.patch(ENDPOINTS.PARENT_LINK_PHONE(studentId), { parentPhone });

/** Costs nothing to ask; the answer is what the confirm dialog shows. */
export const previewBulkParentInvite = (language) =>
  apiClient.get(ENDPOINTS.PARENT_LINK_BULK_PREVIEW, { params: { language } });

/**
 * The server rejects this unless `confirmedRecipients` matches a preview it has
 * just recomputed, so this call can only follow a cost dialog the admin saw.
 */
export const sendBulkParentInvite = (language, confirmedRecipients) =>
  apiClient.post(ENDPOINTS.PARENT_LINK_BULK, { language, confirmedRecipients });

export default apiClient;